/** Cache keys for the organization console's reads. */
export const organizationKeys = {
  all: ['organization'] as const,
  /** The organization's own identity — name and environment. */
  detail: () => ['organization', 'detail'] as const,
  /** The escalation delay and its change history (spec 138). */
  notificationPolicy: () => ['organization', 'notification-policy'] as const,
  /**
   * Every workspace's spend and applied limit, for a period.
   *
   * The period is part of the key for the same reason it is on the workspace page: a
   * limit set today changes what the *current* period looks like and nothing about a
   * past one, so invalidating after a change must not discard months already read.
   */
  usage: (period: string | undefined) =>
    ['organization', 'usage', period ?? 'current'] as const,
  /**
   * Every workflow that has a spend limit, and what it has spent, for a period.
   *
   * Keyed by period like the two above, and for the same reason: setting a cap today
   * changes what the *current* period's table says and nothing about a past one, so
   * invalidating after a change must not discard months already read.
   */
  workflowLimits: (period: string | undefined) =>
    ['organization', 'workflow-limits', period ?? 'current'] as const,
  /**
   * How many runs every workspace may execute at once, and its load (spec 115). Not
   * period-scoped: a concurrency limit is a standing rule, not a budget.
   */
  concurrency: () => ['organization', 'concurrency'] as const,
  /** Every workspace's memory amount (spec 149). A standing rule, not a budget, so not period-scoped. */
  memory: () => ['organization', 'memory'] as const,
  /** The workspaces that require each person's own forge account (spec 160). */
  forgePolicies: () => ['organization', 'forge-policies'] as const,
  /** Each forge host's sign-in app, and the address it returns to (spec 161). */
  forgeApps: () => ['organization', 'forge-apps'] as const,
  /**
   * Everyone and their linked forge accounts, every page read (spec 162 R1).
   * Under the `people-links` prefix, so a revoke or a policy change that
   * invalidates the people list refreshes this too.
   */
  peopleDirectory: () => ['organization', 'people-links', 'all'] as const,
  /** Everyone and their linked forge accounts, a search and a page at a time. */
  peopleLinks: (q?: string, page?: number) =>
    q === undefined && page === undefined
      ? (['organization', 'people-links'] as const)
      : (['organization', 'people-links', q ?? '', page ?? 1] as const),
};
